import { useEffect, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
  hasAnyImageQualityIssuesSelector,
  imageQualityThresholdSelector,
  showImageQualityModalAtom,
  showImageQualityAcknowledgeButtonAtom,
  initialImageQualityCheckCompletedAtom,
  hasShownPageLoadQualityModalAtom,
} from '@/atoms/imageQualityAtoms'

type UsePageLoadImageQualityCheckArgs = {
  checkDelayMilliseconds?: number
  enabled?: boolean
}

/**
 * Hook to automatically show image quality modal on page load if quality issues are detected.
 *
 * This hook monitors the image quality state and automatically triggers the modal when:
 * 1. Initial page load completes (after a configurable delay)
 * 2. Quality issues are detected in any images
 * 3. The modal hasn't already been shown for this page load session
 *
 * The delay allows time for all PageImage components to mount and run their quality checks.
 * This prevents showing the modal before all images have been processed.
 *
 * NOTE: Design assets (subtype === 'designAsset') are automatically excluded from quality
 * checks at the PageImage level, so they will never trigger this modal.
 */
export const usePageLoadImageQualityCheck = ({
  checkDelayMilliseconds = 1000,
  enabled = true,
}: UsePageLoadImageQualityCheckArgs = {}) => {
  const [initialDelayCompleted, setInitialDelayCompleted] = useState(false)
  const hasAnyImageQualityIssues = useRecoilValue(
    hasAnyImageQualityIssuesSelector,
  )
  const imageQualityThreshold = useRecoilValue(imageQualityThresholdSelector)
  const setShowImageQualityModal = useSetRecoilState(showImageQualityModalAtom)
  const setShowImageQualityAcknowledgeButton = useSetRecoilState(
    showImageQualityAcknowledgeButtonAtom,
  )
  const setInitialImageQualityCheckCompleted = useSetRecoilState(
    initialImageQualityCheckCompletedAtom,
  )
  const hasShownPageLoadQualityModal = useRecoilValue(
    hasShownPageLoadQualityModalAtom,
  )
  const setHasShownPageLoadQualityModal = useSetRecoilState(
    hasShownPageLoadQualityModalAtom,
  )

  // Set up initial delay timer on mount
  useEffect(() => {
    if (!enabled) return

    const delayTimer = setTimeout(() => {
      setInitialDelayCompleted(true)
      setInitialImageQualityCheckCompleted(true)
    }, checkDelayMilliseconds)

    return () => {
      clearTimeout(delayTimer)
    }
  }, [enabled, checkDelayMilliseconds, setInitialImageQualityCheckCompleted])

  // Check for quality issues once delay is complete
  useEffect(() => {
    const shouldCheckForQualityIssues =
      enabled &&
      initialDelayCompleted &&
      hasAnyImageQualityIssues &&
      !hasShownPageLoadQualityModal

    if (shouldCheckForQualityIssues) {
      // Show the modal with no acknowledge button (informational only on page load)
      setShowImageQualityModal(true)
      setShowImageQualityAcknowledgeButton(false) // Page load modal is informational only
      setHasShownPageLoadQualityModal(true) // Prevent showing again this session
    }
  }, [
    enabled,
    initialDelayCompleted,
    hasAnyImageQualityIssues,
    hasShownPageLoadQualityModal,
    setShowImageQualityModal,
    setShowImageQualityAcknowledgeButton,
    setHasShownPageLoadQualityModal,
  ])

  // Reset the "has shown" flag when component unmounts (new page load)
  useEffect(() => {
    return () => {
      setHasShownPageLoadQualityModal(false)
      setInitialImageQualityCheckCompleted(false)
    }
  }, [setHasShownPageLoadQualityModal, setInitialImageQualityCheckCompleted])

  return {
    hasCompletedInitialCheck: initialDelayCompleted,
    hasImageQualityIssues: hasAnyImageQualityIssues,
    currentThreshold: imageQualityThreshold,
  }
}
